import type { StoreFeatureFlags } from './permissions.js';

/** Keys match SettingsLayout nav paths (without /settings/ prefix, kebab → snake). */
export type SettingsSectionKey =
  | 'store'
  | 'printing'
  | 'payments'
  | 'integrations'
  | 'tickets'
  | 'notifications'
  | 'appointments'
  | 'waivers'
  | 'loyalty'
  | 'cash_sessions'
  | 'staff'
  | 'permissions'
  | 'pins'
  | 'audit_log'
  | 'approvals'
  | 'discounts'
  | 'repair_services'
  | 'inventory_categories'
  | 'suppliers'
  | 'pricing_matrix'
  | 'warranty_rules'
  | 'forms'
  | 'templates'
  | 'snippets'
  | 'fee_presets'
  | 'conditions'
  | 'devices'
  | 'pos'
  | 'care_guides'
  | 'upsell'
  | 'duplicate_customers'
  | 'profile'
  | 'locations'
  | 'migration'
  | 'data_cleanup'
  | 'billing';

export type SettingsSectionAccess = {
  viewRoles: string[];
  editRoles: string[];
};

const ALL_STAFF = [
  'read_only',
  'front_desk',
  'technician',
  'store_manager',
  'tenant_admin',
  'platform_admin',
] as const;

const MANAGERS = ['store_manager', 'tenant_admin', 'platform_admin'] as const;
const TENANT_ADMINS = ['tenant_admin', 'platform_admin'] as const;

function access(
  viewRoles: readonly string[],
  editRoles: readonly string[],
): SettingsSectionAccess {
  return { viewRoles: [...viewRoles], editRoles: [...editRoles] };
}

/** Defaults: most pages viewable by all staff; editable by managers. Sensitive pages tenant-admin only. */
export const DEFAULT_SETTINGS_SECTIONS: Record<SettingsSectionKey, SettingsSectionAccess> = {
  store: access(ALL_STAFF, MANAGERS),
  printing: access(ALL_STAFF, MANAGERS),
  payments: access(ALL_STAFF, MANAGERS),
  integrations: access(ALL_STAFF, MANAGERS),
  tickets: access(ALL_STAFF, MANAGERS),
  notifications: access(ALL_STAFF, MANAGERS),
  appointments: access(ALL_STAFF, MANAGERS),
  waivers: access(ALL_STAFF, MANAGERS),
  loyalty: access(ALL_STAFF, MANAGERS),
  cash_sessions: access(ALL_STAFF, MANAGERS),
  staff: access(ALL_STAFF, MANAGERS),
  permissions: access(TENANT_ADMINS, TENANT_ADMINS),
  pins: access(ALL_STAFF, MANAGERS),
  audit_log: access(TENANT_ADMINS, TENANT_ADMINS),
  approvals: access(MANAGERS, MANAGERS),
  discounts: access(ALL_STAFF, MANAGERS),
  repair_services: access(ALL_STAFF, MANAGERS),
  inventory_categories: access(ALL_STAFF, MANAGERS),
  suppliers: access(ALL_STAFF, MANAGERS),
  pricing_matrix: access(MANAGERS, MANAGERS),
  warranty_rules: access(ALL_STAFF, MANAGERS),
  forms: access(ALL_STAFF, MANAGERS),
  templates: access(ALL_STAFF, MANAGERS),
  snippets: access(ALL_STAFF, MANAGERS),
  fee_presets: access(ALL_STAFF, MANAGERS),
  conditions: access(ALL_STAFF, MANAGERS),
  devices: access(ALL_STAFF, MANAGERS),
  pos: access(ALL_STAFF, MANAGERS),
  care_guides: access(ALL_STAFF, MANAGERS),
  upsell: access(ALL_STAFF, MANAGERS),
  duplicate_customers: access(MANAGERS, MANAGERS),
  profile: access(ALL_STAFF, ALL_STAFF),
  locations: access(TENANT_ADMINS, TENANT_ADMINS),
  migration: access(TENANT_ADMINS, TENANT_ADMINS),
  data_cleanup: access(TENANT_ADMINS, TENANT_ADMINS),
  billing: access(ALL_STAFF, TENANT_ADMINS),
};

export const SETTINGS_SECTION_META: Array<{
  key: SettingsSectionKey;
  label: string;
  group: string;
}> = [
  { key: 'store', label: 'Store Info', group: 'Store' },
  { key: 'printing', label: 'Printing & Display', group: 'Store' },
  { key: 'payments', label: 'Payments & Tax', group: 'Store' },
  { key: 'integrations', label: 'Integrations', group: 'Store' },
  { key: 'tickets', label: 'Ticket Settings', group: 'Operations' },
  { key: 'notifications', label: 'Notifications', group: 'Operations' },
  { key: 'appointments', label: 'Appointments', group: 'Operations' },
  { key: 'waivers', label: 'Waivers', group: 'Operations' },
  { key: 'loyalty', label: 'Loyalty & Referrals', group: 'Operations' },
  { key: 'cash_sessions', label: 'Cash Sessions', group: 'Operations' },
  { key: 'staff', label: 'Staff', group: 'Staff & Security' },
  { key: 'permissions', label: 'Permissions', group: 'Staff & Security' },
  { key: 'pins', label: 'PIN Settings', group: 'Staff & Security' },
  { key: 'approvals', label: 'Remote Approvals', group: 'Staff & Security' },
  { key: 'audit_log', label: 'Audit Log', group: 'Staff & Security' },
  { key: 'repair_services', label: 'Repair Services', group: 'Customization' },
  { key: 'inventory_categories', label: 'Inventory Categories', group: 'Customization' },
  { key: 'suppliers', label: 'Suppliers', group: 'Customization' },
  { key: 'discounts', label: 'Discount Presets', group: 'Customization' },
  { key: 'pricing_matrix', label: 'Pricing Matrix', group: 'Customization' },
  { key: 'warranty_rules', label: 'Warranty Rules', group: 'Customization' },
  { key: 'forms', label: 'Custom Forms', group: 'Customization' },
  { key: 'templates', label: 'Ticket Templates', group: 'Customization' },
  { key: 'snippets', label: 'Note Snippets', group: 'Customization' },
  { key: 'fee_presets', label: 'Fee Presets', group: 'Customization' },
  { key: 'conditions', label: 'Device Conditions', group: 'Customization' },
  { key: 'devices', label: 'Device Catalog', group: 'Customization' },
  { key: 'pos', label: 'POS Settings', group: 'Customization' },
  { key: 'care_guides', label: 'Care Guides', group: 'Customization' },
  { key: 'upsell', label: 'Upsell Suggestions', group: 'Customization' },
  { key: 'duplicate_customers', label: 'Duplicate Customers', group: 'Customization' },
  { key: 'profile', label: 'My Profile', group: 'Account' },
  { key: 'locations', label: 'Locations', group: 'Account' },
  { key: 'migration', label: 'Data Migration', group: 'Account' },
  { key: 'data_cleanup', label: 'Data Cleanup', group: 'Account' },
  { key: 'billing', label: 'Billing', group: 'Account' },
];

/** Map /settings/:slug path segment to section key. */
export function settingsPathToSectionKey(pathname: string): SettingsSectionKey | null {
  const match = pathname.match(/^\/settings\/([^/]+)/);
  if (!match?.[1]) return null;
  const slug = match[1];
  const map: Record<string, SettingsSectionKey> = {
    store: 'store',
    printing: 'printing',
    payments: 'payments',
    integrations: 'integrations',
    tickets: 'tickets',
    'ticket-statuses': 'tickets',
    notifications: 'notifications',
    appointments: 'appointments',
    waivers: 'waivers',
    loyalty: 'loyalty',
    'cash-sessions': 'cash_sessions',
    staff: 'staff',
    permissions: 'permissions',
    pins: 'pins',
    approvals: 'approvals',
    'audit-log': 'audit_log',
    'repair-services': 'repair_services',
    'inventory-categories': 'inventory_categories',
    suppliers: 'suppliers',
    discounts: 'discounts',
    'pricing-matrix': 'pricing_matrix',
    'warranty-rules': 'warranty_rules',
    forms: 'forms',
    templates: 'templates',
    snippets: 'snippets',
    'fee-presets': 'fee_presets',
    conditions: 'conditions',
    devices: 'devices',
    pos: 'pos',
    'care-guides': 'care_guides',
    upsell: 'upsell',
    'duplicate-customers': 'duplicate_customers',
    profile: 'profile',
    locations: 'locations',
    migration: 'migration',
    'data-cleanup': 'data_cleanup',
    billing: 'billing',
  };
  return map[slug] ?? null;
}

export function mergeSettingsSections(
  flags: StoreFeatureFlags | null | undefined,
): Record<SettingsSectionKey, SettingsSectionAccess> {
  const raw = flags?.settings_sections ?? {};
  const out = { ...DEFAULT_SETTINGS_SECTIONS };
  for (const key of Object.keys(DEFAULT_SETTINGS_SECTIONS) as SettingsSectionKey[]) {
    const override = raw[key];
    if (!override) continue;
    out[key] = {
      viewRoles: override.viewRoles?.length
        ? [...override.viewRoles]
        : [...DEFAULT_SETTINGS_SECTIONS[key].viewRoles],
      editRoles: override.editRoles?.length
        ? [...override.editRoles]
        : [...DEFAULT_SETTINGS_SECTIONS[key].editRoles],
    };
  }
  return out;
}

export function canViewSettingsSection(
  flags: StoreFeatureFlags | null | undefined,
  userRole: string,
  section: SettingsSectionKey,
): boolean {
  if (userRole === 'platform_admin') return true;
  const access = mergeSettingsSections(flags)[section];
  return access.viewRoles.includes(userRole);
}

export function canEditSettingsSection(
  flags: StoreFeatureFlags | null | undefined,
  userRole: string,
  section: SettingsSectionKey,
): boolean {
  if (userRole === 'platform_admin') return true;
  const access = mergeSettingsSections(flags)[section];
  return access.viewRoles.includes(userRole) && access.editRoles.includes(userRole);
}

/** True if the role can edit at least one store-settings section (for PATCH /stores/current). */
export function canEditAnyStoreSettings(
  flags: StoreFeatureFlags | null | undefined,
  userRole: string,
): boolean {
  if (userRole === 'platform_admin') return true;
  const sections = mergeSettingsSections(flags);
  const patchable: SettingsSectionKey[] = [
    'store',
    'printing',
    'payments',
    'integrations',
    'tickets',
    'notifications',
    'appointments',
    'waivers',
    'loyalty',
    'cash_sessions',
    'pins',
    'pos',
    'permissions',
  ];
  return patchable.some((key) => sections[key].editRoles.includes(userRole));
}
